/** @format */

import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ChildCareOutlined from '@mui/icons-material/ChildCareOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DrawOutlined from '@mui/icons-material/DrawOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import InsertPhotoOutlined from '@mui/icons-material/InsertPhotoOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DocumentsViewDto } from '../../../Models';
import DocumentModal from './documentsModal';

interface Props {
  document: DocumentsViewDto;
}

function pickIconForDocument(doc: DocumentsViewDto): SvgIconComponent {
  const name = (doc.documentTypeName || '').toLowerCase();
  const ext = (doc.extension || '').toLowerCase();

  if (ext.includes('image')) return InsertPhotoOutlined;
  if (ext.includes('pdf')) return PictureAsPdfOutlined;

  if (name.includes('training') && name.includes('contract'))
    return AssignmentTurnedInOutlined;
  if (name.includes('grade 11') || name.includes('grade 12')) return SchoolOutlined;
  if (name.includes('academic')) return SchoolOutlined;
  if (name.includes('registration')) return DrawOutlined;
  if (name.includes('consent')) return GavelOutlined;
  if (
    name.includes('guardian') ||
    name.includes('father') ||
    name.includes('mother') ||
    name.includes('applicant id') ||
    name.includes('dependent') ||
    name.includes('identity')
  )
    return BadgeOutlined;
  if (
    name.includes('payslip') ||
    name.includes('bank') ||
    name.includes('cipc') ||
    name.includes('business')
  )
    return AccountBalanceOutlined;
  if (
    name.includes('sassa') ||
    name.includes('labour') ||
    name.includes('affidavit')
  )
    return WorkOutlineOutlined;
  if (name.includes('dependent')) return ChildCareOutlined;

  return DescriptionOutlined;
}

export default function ExtensionChecker({ document }: Props) {
  const theme = useTheme();
  const hasFile = Boolean(document.link);
  const Icon = pickIconForDocument(document);
  const iconColor = hasFile ? 'primary.main' : 'text.disabled';

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: hasFile ? alpha(theme.palette.primary.main, 0.22) : 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        transition:
          'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: hasFile ? theme.shadows[4] : theme.shadows[1],
          transform: hasFile ? 'translateY(-2px)' : 'none',
        },
      }}>
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          px: 1.5,
          pt: 2,
          pb: 2,
          overflow: 'visible',
          '&:last-child': { pb: 2 },
        }}>
        <Stack spacing={1.25} alignItems='center' sx={{ width: '100%' }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: hasFile
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.action.disabled, 0.08),
            }}>
            <Icon sx={{ fontSize: 30, color: iconColor }} />
          </Box>

          {!hasFile ? (
            <Box sx={{ width: '100%' }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  lineHeight: 1.45,
                  wordBreak: 'break-word',
                  display: 'block',
                }}>
                {document.documentTypeName}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Chip
                  label='Pending upload'
                  size='small'
                  color='warning'
                  variant='outlined'
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
              <DocumentModal document={document} />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
